import { DataBaseModelInterface } from '@solcre-org/core';
import { PortTariffModel } from './port-tariff.model';
import { PortTariffLineTypes } from '../enums/port-tariff-line-types.enum';
import { PortTariffLineVesselProperties } from '../enums/port-tariff-line-vessel-properties.enum';
import { PortTariffLineExpenseCategories } from '../enums/port-tariff-line-expense-category.enum';
import { PortTariffLineOperationDirections } from '../enums/port-tariff-line-operation-direction.enum';

export class PortTariffLineModel implements DataBaseModelInterface {
  public tariffDetails?: PortTariffModel;

  constructor(
    public id?: any,
    public tariffId?: any,
    public tariff?: string,
    public sortOrder?: number,
    public concept?: string,
    public lineType?: string,
    public rateValue?: string | null,
    public unitType?: string | null,
    public vesselProperty?: string | null,
    public expenseCategory?: string,
    public operationDirection?: string,
    public cargoType?: string | null,
    public applicableVessel?: string | null,
    public quantityMultiplier?: string | null,
    public isActive: boolean = true,
    public archivedAt?: string | null,
    public createdAt?: string,
    public updatedAt?: string,
    public ot1RateValue?: string | null,
    public ot1UnitType?: string | null,
    public ot1VesselProperty?: string | null,
    public ot1QuantityMultiplier?: string | null,
    public ot1CargoType?: string | null,
    public ot2RateValue?: string | null,
    public ot2UnitType?: string | null,
    public ot2VesselProperty?: string | null,
    public ot2QuantityMultiplier?: string | null,
    public ot2CargoType?: string | null
  ) {}

  public fromJSON(json: any): PortTariffLineModel {
    if (json) {
      this.id = json.id;
      this.tariffId = json.tariffId ?? json.tariff?.id ?? json.portTariffId;
      this.tariff = this.extractTariffIri(json.tariff ?? json.portTariff);
      this.sortOrder = Number(json.sortOrder ?? 0);
      this.concept = json.concept ?? json.description;
      this.lineType = json.lineType ?? (json.isTax ? PortTariffLineTypes.TAX : PortTariffLineTypes.STANDARD);
      this.rateValue = this.normalizeDecimalOrNull(json.rateValue ?? json.variable1Amount);
      this.unitType = json.unitType ?? json.variable2UnitType ?? null;
      this.vesselProperty = json.vesselProperty ?? json.variable3VesselProperty ?? PortTariffLineVesselProperties.NONE;
      this.expenseCategory = json.expenseCategory ?? PortTariffLineExpenseCategories.PORT;
      this.operationDirection = json.operationDirection ?? PortTariffLineOperationDirections.BOTH;
      this.cargoType = json.cargoType ?? null;
      this.applicableVessel = this.extractVesselIri(json.applicableVessel);
      this.quantityMultiplier = this.normalizeDecimalOrNull(json.quantityMultiplier) ?? '1.0000';
      this.isActive = json.isActive ?? true;
      this.archivedAt = json.archivedAt ?? null;
      this.createdAt = json.createdAt;
      this.updatedAt = json.updatedAt;

      this.ot1RateValue = this.normalizeDecimalOrNull(json.ot1RateValue);
      this.ot1UnitType = json.ot1UnitType ?? null;
      this.ot1VesselProperty = json.ot1VesselProperty ?? null;
      this.ot1QuantityMultiplier = this.normalizeDecimalOrNull(json.ot1QuantityMultiplier);
      this.ot1CargoType = json.ot1CargoType ?? null;

      this.ot2RateValue = this.normalizeDecimalOrNull(json.ot2RateValue);
      this.ot2UnitType = json.ot2UnitType ?? null;
      this.ot2VesselProperty = json.ot2VesselProperty ?? null;
      this.ot2QuantityMultiplier = this.normalizeDecimalOrNull(json.ot2QuantityMultiplier);
      this.ot2CargoType = json.ot2CargoType ?? null;

      if (typeof json.tariff === 'object' && json.tariff !== null) {
        this.tariffDetails = new PortTariffModel().fromJSON(json.tariff);
      } else if (typeof json.portTariff === 'object' && json.portTariff !== null) {
        this.tariffDetails = new PortTariffModel().fromJSON(json.portTariff);
      }
    }

    return this;
  }

  public fromJSONList(json: any): PortTariffLineModel[] {
    if (json) {
      return json.map((element: any) => new PortTariffLineModel().fromJSON(element));
    }
    return [];
  }

  public toJSON() {
    return {
      id: this.id,
      tariffId: this.tariffId,
      tariff: this.tariff,
      sortOrder: this.sortOrder,
      concept: this.concept,
      lineType: this.lineType,
      rateValue: this.rateValue,
      unitType: this.unitType,
      vesselProperty: this.vesselProperty,
      expenseCategory: this.expenseCategory,
      operationDirection: this.operationDirection,
      cargoType: this.cargoType,
      applicableVessel: this.applicableVessel,
      quantityMultiplier: this.quantityMultiplier,
      isActive: this.isActive,
      archivedAt: this.archivedAt,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
      ot1RateValue: this.ot1RateValue,
      ot1UnitType: this.ot1UnitType,
      ot1VesselProperty: this.ot1VesselProperty,
      ot1QuantityMultiplier: this.ot1QuantityMultiplier,
      ot1CargoType: this.ot1CargoType,
      ot2RateValue: this.ot2RateValue,
      ot2UnitType: this.ot2UnitType,
      ot2VesselProperty: this.ot2VesselProperty,
      ot2QuantityMultiplier: this.ot2QuantityMultiplier,
      ot2CargoType: this.ot2CargoType,
    };
  }

  public getId() {
    return this.id ?? '';
  }

  public getReference() {
    return this.concept ?? this.id ?? '';
  }

  // Preserves null (empty rate) instead of collapsing it to "0.0000" — a block with
  // no rate must stay distinguishable from a block priced at USD 0.
  private normalizeDecimalOrNull(value: any): string | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) {
      return String(value);
    }

    return numericValue.toFixed(4);
  }

  private extractVesselIri(value: any): string | null {
    if (!value) {
      return null;
    }

    if (typeof value === 'string') {
      return value;
    }

    const reference = value['@id'] ?? value.id;
    return typeof reference === 'string' ? reference : null;
  }

  private extractTariffIri(value: any): string {
    if (!value) {
      return '';
    }

    if (typeof value === 'string') {
      return value;
    }

    const reference = value['@id'] ?? value.id;
    if (typeof reference === 'string' && reference.startsWith('/api/')) {
      return reference;
    }

    return reference ? `/api/port-tariffs/${reference}` : '';
  }
}
